import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { ContactsAddIcon } from '@dev/ee-components';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import { links } from '@/services/dictionaries/links';
import SvgSegments from '@/services/Layout/icons/segments.svg';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { ALL_CONTACTS_SEGMENT } from '@/services/constants';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';

const taskId = 'importYourContacts';
const itemVideoId = 'Lub5_XLxfYE';

export enum ImportYourContactsTaskSavedDataKeysEnum {
    ImportYourContactsCheckContacts = 'ImportYourContactsCheckContacts',
    ImportYourContactsSkipStep1 = 'ImportYourContactsSkipStep1',
    ImportYourContactsSkipStep2 = 'ImportYourContactsSkipStep2',
}

export const useImportYourContactsTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);

    const goToUploadContacts = (): void => {
        dispatch(push(ContactsPaths.ContactsUpload));
    };

    const goToSingleContacts = (): void => {
        dispatch(push(ContactsPaths.ContactsAdd));
    };

    const goToContacts = (): void => {
        if (!savedData?.ImportYourContactsCheckContacts) {
            dispatch(SaveOnboardingStart({ ImportYourContactsCheckContacts: true }));
        }
        dispatch(
            push(ContactsPaths.SingleSegment.replace(':segmentname', safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)))
        );
    };

    const importYourContactsSteps: OnboardingSteps = {
        id: taskId,
        title: t('addexistingcontacts'),
        items: [
            {
                title: t('addexistingcontacts'),
                features: [
                    t('importyourcontacts_addexistingcontacts_feature1'),
                    t('importyourcontacts_addexistingcontacts_feature2'),
                ],
                tip: (
                    <Trans
                        i18nKey="importyourcontacts_addexistingcontacts_tip"
                        components={{ url: <Link href={links.deliveryGuide} target="_blank" /> }}
                    />
                ),
                secondaryContent: <OnboardingVideoThumbnail videoId={itemVideoId} />,
                primaryActionTitle: t('uploadcontacts'),
                primaryActionCallback: goToUploadContacts,
                secondaryActionTitle: t('addsinglecontact'),
                secondaryActionCallback: goToSingleContacts,
                isSkipped: !!savedData?.ImportYourContactsSkipStep1,
                skipSavedDataKey: ImportYourContactsTaskSavedDataKeysEnum.ImportYourContactsSkipStep1,
                isCompleted: data?.AtLeastTwoContacts,
            },
            {
                title: t('findoutmoreaboutcontactstatuses'),
                features: [
                    <Trans
                        key="importyourcontacts_findoutmore_feature1"
                        i18nKey="importyourcontacts_findoutmore_feature1"
                        components={{ url: <Link href={links.contactStatuses} target="_blank" /> }}
                    />,
                    <Trans
                        key="importyourcontacts_findoutmore_feature2"
                        i18nKey="importyourcontacts_findoutmore_feature2"
                        components={{ url: <Link href={links.contactListsAndSegments} target="_blank" /> }}
                    />,
                ],
                secondaryContent: <SvgSegments />,
                primaryActionTitle: t('checkcontacts'),
                primaryActionCallback: goToContacts,
                isNotReady: !!savedData?.ImportYourContactsSkipStep1 || !data?.AtLeastTwoContacts,
                isSkipped: !!savedData?.ImportYourContactsSkipStep2,
                skipSavedDataKey: ImportYourContactsTaskSavedDataKeysEnum.ImportYourContactsSkipStep2,
                isCompleted: !!savedData?.ImportYourContactsCheckContacts,
            },
        ],
    };

    return {
        header: t('importyourcontacts'),
        icon: <ContactsAddIcon />,
        title: t('addcontactstoyouraccount'),
        description: t('addcontactstoyouraccount_desc'),
        steps: [importYourContactsSteps],
        activeStepsIndex: 0,
        isVisible: onboardingGoals.isSendingMarketingEmailsAnswered || onboardingGoals.isSendingNewslettersAnswered,
    };
};
